/**
 * ProductFruits Messaging Module
 * Handles all postMessage communication between parent and iframes
 */

import {
  isIframeReadyMessage,
  isTourEventMessage,
  isUserPropertyUpdateMessage,
} from './types';
import { serializeError } from './utils';

/**
 * Type guard to check if MessageEvent.source is a Window
 * (as opposed to MessagePort, ServiceWorker, or null)
 */
function isWindowSource(source) {
  if (source === null || typeof source !== 'object') {
    return false;
  }

  // Check for Window-specific properties
  // Window has postMessage (function) and location (object)
  const hasPostMessage =
    'postMessage' in source &&
    typeof source.postMessage === 'function';
  const hasLocation = 'location' in source && source.location !== null;

  return hasPostMessage && hasLocation;
}

/**
 * Manages postMessage communication for ProductFruits
 */
export class ProductFruitsMessaging {
  constructor(handler) {
    this.handler = handler;
    this.boundHandleMessage = this.handleMessage.bind(this);
    window.addEventListener('message', this.boundHandleMessage);
  }

  destroy() {
    window.removeEventListener('message', this.boundHandleMessage);
  }

  /**
   * Send USER_AUTHENTICATED message to iframe
   */
  sendUserAuthenticated(iframeWindow, userData, shouldInitProductFruits) {
    const payload = {
      type: 'USER_AUTHENTICATED',
      userData,
      shouldInitProductFruits,
      timestamp: new Date().toISOString(),
    };

    try {
      iframeWindow.postMessage(payload, window.location.origin);
    } catch (error) {
      this.emitDebugEvent('postmessage-failed', {
        error: this.serializeError(error),
      });
      throw error;
    }
  }

  /**
   * Broadcast tour event to all iframes except source
   */
  broadcastTourEvent(iframeWindows, message) {
    this.emitDebugEvent('tour-event', {
      tourId: message.tourId,
      source: message.source,
    });

    iframeWindows.forEach((iframeWindow, appName) => {
      if (message.source && message.source === appName) {
        return;
      }

      iframeWindow.postMessage(
        {
          ...message,
          source: 'parent',
          timestamp: new Date().toISOString(),
        },
        window.location.origin
      );
    });
  }

  /**
   * Broadcast property update to all iframes except source
   */
  broadcastPropertyUpdate(iframeWindows, props, source) {
    iframeWindows.forEach((iframeWindow, appName) => {
      if (source && source === appName) {
        return;
      }

      iframeWindow.postMessage(
        {
          type: 'USER_PROPERTY_UPDATE',
          props,
          source: 'parent',
          timestamp: new Date().toISOString(),
        },
        window.location.origin
      );
    });
  }

  handleMessage(event) {
    if (event.origin !== window.location.origin) {
      this.emitDebugEvent('unauthorized-origin', { origin: event.origin });
      return;
    }

    const data = event.data;

    // Validate basic structure before type narrowing
    if (
      !data ||
      typeof data !== 'object' ||
      !('type' in data) ||
      typeof data.type !== 'string'
    ) {
      return;
    }

    // Use type guards to safely narrow the type
    if (isIframeReadyMessage(data)) {
      // Validate event.source is a Window (not MessagePort, ServiceWorker, or null)
      if (isWindowSource(event.source)) {
        this.handler.onIframeReady(data.app, event.source);
      }
      return;
    }

    if (isTourEventMessage(data)) {
      this.handler.onTourEvent(data);
      return;
    }

    if (isUserPropertyUpdateMessage(data)) {
      this.handler.onUserPropertyUpdate(data.props, data.source);
      return;
    }
  }

  serializeError(error) {
    return serializeError(error);
  }

  emitDebugEvent(eventName, payload = {}) {
    if (typeof window === 'undefined') {
      return;
    }

    window.dispatchEvent(
      new CustomEvent('productfruits:debug', {
        detail: {
          eventName,
          ...payload,
        },
      })
    );
  }
}

